import { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import axios from 'axios';

import { getErrorMessage } from '@ts-core/lib/apollo';
import { getSignUrlQuery } from 'core/graphql/queries';

import { useProfessorAuth } from 'modules/auth/hooks';

import { UploadProgressState } from 'core/components/UploadProgressIndicator';

export default function useFileUploader({ onFileUploaded, getFileType }) {
  const apolloClient = useApolloClient();
  const { user } = useProfessorAuth();

  const academy = user?.academy;

  const [error, setError] = useState();
  const [state, setState] = useState(UploadProgressState.idle);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileName, setFileName] = useState();

  const uploadFile = useCallback(
    async (file) => {
      const [name] = file.name.split('.');

      setError();
      setState(UploadProgressState.processing);
      setFileName(name);

      try {
        const { data } = await apolloClient.query({
          query: getSignUrlQuery,
          variables: {
            fileType: getFileType(file),
            contentType: file.type,
          },
          fetchPolicy: 'network-only',
        });

        setState(UploadProgressState.uploading);

        const { path, signedRequest, url, awsFileName } = data?.signedUrlPayload;

        const options = {
          headers: {
            'Content-Type': file.type,
            'x-amz-tagging': `academy=${academy?._id}`,
          },
          onUploadProgress: (progressEvent) => {
            if (!progressEvent.total) return;
            const progress = (progressEvent.loaded / progressEvent.total) * 100;
            setUploadProgress(progress);
          },
        };

        await axios.put(signedRequest, file, options);

        return { path, url, name, awsFileName };
      } catch (uploadError) {
        setState(UploadProgressState.failed);
        setError(getErrorMessage({ error: uploadError }));
        return {};
      }
    },
    [getFileType, apolloClient, academy]
  );

  const onFileSelected = useCallback(
    async (file) => {
      if (state !== UploadProgressState.idle) return;

      const { path, name, url, awsFileName } = await uploadFile(file);

      if (path) {
        await onFileUploaded({
          input: { name, url, awsFileName, path },
          setState,
          setError,
        });
      }
    },

    [onFileUploaded, uploadFile, state]
  );

  const closeProgressIndicator = useCallback(() => {
    setState(UploadProgressState.idle);
  }, []);

  return {
    changeState: setState,
    setError,

    state,
    fileName,
    error,
    progress: uploadProgress,

    onFileSelected,
    onClose: closeProgressIndicator,
  };
}
